import type { SectionId } from '../constants/wizard';
import { trackOptimizelyEvent, trackRentalApplicationEvent, trackTermEvent } from './index';
import type { ApplicationContextValue } from '../contexts/ApplicationContext';
import { getRentalApplication } from './getRentalApplication';
import { getRentalApplicationTerm } from './getRentalApplicationTerm';
import { getZrmListingInfo } from './getZrmListingInfo';

function getEnvelope({
  eventTypeId,
  eventTypeVersionId = '1',
  eventTemplateId = '71',
  eventTemplateVersionId = '1',
}: {
  eventTypeId: string;
  eventTypeVersionId?: string;
  eventTemplateId?: string;
  eventTemplateVersionId?: string;
}) {
  return {
    event_template_id: eventTemplateId,
    event_template_version_id: eventTemplateVersionId,
    event_type_id: eventTypeId,
    event_type_version_id: eventTypeVersionId,
    event_client_start_dtm: new Date().toISOString(),
  };
}

function getTrigger({
  triggerSourceName,
  triggerObjectName = 'rental_application_object',
  triggerLocationName = 'rental_application',
  triggerType = 'interaction',
  inputSelectorName = '',
  triggerReferenceUrl = '',
  referralUrl = '',
}: {
  triggerSourceName: string;
  triggerObjectName?: string;
  triggerLocationName?: string;
  triggerType?: 'impression' | 'interaction' | 'view';
  inputSelectorName?: string;
  triggerReferenceUrl?: string;
  referralUrl?: string;
}) {
  return {
    trigger_location_nm: triggerLocationName,
    trigger_type_nm: triggerType,
    trigger_object_nm: triggerObjectName,
    trigger_source_nm: triggerSourceName,
    input_selector_nm: inputSelectorName,
    trigger_reference_url: triggerReferenceUrl,
    referral_url: referralUrl,
  };
}

function getSemantic({
  semanticEventName,
  topicTags = ['rental_application'],
}: {
  semanticEventName: string;
  topicTags?: Array<string>;
}) {
  return {
    semantic_event_nm: semanticEventName,
    topic_tag_txt: topicTags,
  };
}

// instead of using an optional argument `...?: ApplicationContextValue)`
// we want our cs events to require passing application ctx even if it's undefined
// this prevents accidentally omitting data that is actually available
type ApplicationContextOrUndefined = ApplicationContextValue | undefined;

export const CLICKSTREAM_CATEGORY_EVENTS: Record<
  SectionId,
  (applicationContext: ApplicationContextOrUndefined) => void | undefined
> = {
  // https://dataportal.zillowgroup.net/event-registry/2012
  'personal-info': (applicationContext: ApplicationContextOrUndefined) =>
    trackRentalApplicationEvent({
      envelope: getEnvelope({ eventTypeId: '2012' }),
      clickstream_trigger: getTrigger({ triggerSourceName: 'button_to_add_personal_info' }),
      semantic: getSemantic({ semanticEventName: 'visit_application_personal_info_section' }),
      ...getRentalApplication(applicationContext),
    }),
  // https://dataportal.zillowgroup.net/event-registry/2013
  'household-info': (applicationContext: ApplicationContextOrUndefined) =>
    trackRentalApplicationEvent({
      envelope: getEnvelope({ eventTypeId: '2013' }),
      clickstream_trigger: getTrigger({ triggerSourceName: 'button_to_add_household' }),
      semantic: getSemantic({ semanticEventName: 'visit_application_household_section' }),
      ...getRentalApplication(applicationContext),
    }),
  // https://dataportal.zillowgroup.net/event-registry/2014
  'residence-history': (applicationContext: ApplicationContextOrUndefined) =>
    trackRentalApplicationEvent({
      envelope: getEnvelope({ eventTypeId: '2014' }),
      clickstream_trigger: getTrigger({ triggerSourceName: 'button_to_add_residence_history' }),
      semantic: getSemantic({ semanticEventName: 'visit_application_reshistory_section' }),
      ...getRentalApplication(applicationContext),
    }),
  // https://dataportal.zillowgroup.net/event-registry/2015
  'id-verification': (applicationContext: ApplicationContextOrUndefined) =>
    trackRentalApplicationEvent({
      envelope: getEnvelope({ eventTypeId: '2015' }),
      clickstream_trigger: getTrigger({ triggerSourceName: 'button_to_add_idverification' }),
      semantic: getSemantic({ semanticEventName: 'visit_application_idv_section' }),
      ...getRentalApplication(applicationContext),
    }),
  // https://dataportal.zillowgroup.net/event-registry/2016
  'income-history': (applicationContext: ApplicationContextOrUndefined) =>
    trackRentalApplicationEvent({
      envelope: getEnvelope({ eventTypeId: '2016' }),
      clickstream_trigger: getTrigger({ triggerSourceName: 'button_to_add_income_history' }),
      semantic: getSemantic({ semanticEventName: 'visit_application_income_history_section' }),
      ...getRentalApplication(applicationContext),
    }),
  // https://dataportal.zillowgroup.net/event-registry/2313
  'payment-and-reports': (applicationContext: ApplicationContextOrUndefined) =>
    trackRentalApplicationEvent({
      envelope: getEnvelope({ eventTypeId: '2313' }),
      clickstream_trigger: getTrigger({ triggerSourceName: 'button_to_add_screening_section' }),
      semantic: getSemantic({ semanticEventName: 'visit_application_screening_section' }),
      ...getRentalApplication(applicationContext),
    }),
  // TODO: Define ClickstreamEvent for the Review section
  review: () => undefined,
};

// https://dataportal.zillowgroup.net/event-registry/2040
export const emitOverviewSubmitEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '2040' }),
    clickstream_trigger: getTrigger({ triggerSourceName: 'button_to_overview_submit' }),
    semantic: getSemantic({ semanticEventName: 'overview_button_submit' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/146
export const emitPurchaseReportsEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '146' }),
    clickstream_trigger: {
      trigger_location_nm: 'rental_application_submit',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'payment_component',
      trigger_source_nm: 'button_to_purchase',
      input_selector_nm: '',
      trigger_reference_url: '',
      referral_url: '',
    },
    semantic: {
      semantic_event_nm: 'purchase_rental_application',
      topic_tag_txt: ['rental_application', 'purchase'],
    },
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/6487
export const emitApplePayButtonVisibleEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '6487' }),
    clickstream_trigger: {
      trigger_location_nm: 'rental_application',
      trigger_type_nm: 'impression',
      trigger_object_nm: 'rental_application_object_screening_section',
      trigger_source_nm: 'applepay_displays',
      trigger_reference_url: '',
      referral_url: '',
    },
    semantic: getSemantic({ semanticEventName: 'applepay_displays_on_checkout' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/6488
export const emitApplePayPurchaseReportsEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '6488' }),
    clickstream_trigger: {
      trigger_location_nm: 'rental_application',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'rental_application_object_screening_section',
      trigger_source_nm: 'selects_applepay_for_application_checkout',
      input_selector_nm: '',
      trigger_reference_url: '',
      referral_url: '',
    },
    semantic: getSemantic({ semanticEventName: 'selects_applepay_for_application_checkout' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/6489
export const emitApplePaySuccessfulEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '6489' }),
    clickstream_trigger: {
      trigger_location_nm: 'rental_application',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'rental_application_object_screening_section',
      trigger_source_nm: 'applepay_successful_checkout',
      input_selector_nm: '',
      trigger_reference_url: '',
      referral_url: '',
    },
    semantic: getSemantic({ semanticEventName: 'applepay_successful_on_checkout' }),
    ...getRentalApplication(applicationContext),
    ...getZrmListingInfo(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/8445
export const emitNonApplePaySuccessfulEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '8445' }),
    clickstream_trigger: {
      trigger_location_nm: 'rental_application_submit',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'payment_component',
      trigger_source_nm: 'application_purchase_button',
      input_selector_nm: '',
      trigger_reference_url: '',
      referral_url: '',
    },
    semantic: getSemantic({ semanticEventName: 'payment_successful_checkout' }),
    ...getRentalApplication(applicationContext),
    ...getZrmListingInfo(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/5972
export const emitPaymentFormStartsLoadingEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackTermEvent({
    envelope: getEnvelope({ eventTypeId: '5972' }),
    clickstream_trigger: {
      trigger_location_nm: 'rental_application',
      trigger_type_nm: 'impression',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'view_application_payment_screen_loads',
      input_selector_nm: '',
      trigger_reference_url: '',
      referral_url: '',
    },
    semantic: getSemantic({ semanticEventName: 'view_content' }),
    ...getRentalApplicationTerm(),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/5931
export const emitPaymentFormLoadingCompleteEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackTermEvent({
    envelope: getEnvelope({ eventTypeId: '5931' }),
    clickstream_trigger: {
      trigger_location_nm: 'rental_application',
      trigger_type_nm: 'impression',
      trigger_object_nm: 'rental_application_object_screening_section',
      trigger_source_nm: 'view_application_payment',
      input_selector_nm: '',
      trigger_reference_url: '',
      referral_url: '',
    },
    semantic: getSemantic({ semanticEventName: 'view_content' }),
    ...getRentalApplicationTerm(),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/202
export const emitRentalApplicationCompletedEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '202' }),
    clickstream_trigger: {
      trigger_location_nm: 'rental_application_submit',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'rental_application_object',
      trigger_source_nm: 'button_to_submit_application',
      input_selector_nm: 'mouse',
      trigger_reference_url: '',
      referral_url: '',
    },
    semantic: getSemantic({ semanticEventName: 'rental_application_completed' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/201
export const emitViewContentEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '201' }),
    clickstream_trigger: {
      trigger_location_nm: 'rental_application',
      trigger_type_nm: 'view',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'rental_application',
      input_selector_nm: 'mouse',
      trigger_reference_url: '',
      referral_url: '',
    },
    semantic: getSemantic({ semanticEventName: 'view_content' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/3068
export const emitWithdrawModalEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '3068' }),
    clickstream_trigger: getTrigger({ triggerSourceName: 'button_to_withdraw_application' }),
    semantic: getSemantic({ semanticEventName: 'withdraw_application' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/2321
export const emitWithdrawConfirmEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '2321' }),
    clickstream_trigger: getTrigger({ triggerSourceName: 'confirm_withdraw_application' }),
    semantic: getSemantic({ semanticEventName: 'withdraw_application_confirm' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/4043
export const emitStartVoiFlowEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '4043' }),
    clickstream_trigger: getTrigger({ triggerSourceName: 'verify_income_automatically' }),
    semantic: getSemantic({ semanticEventName: 'provide_voi_income' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/4044
export const emitStartManualIncomeFlowEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '4044' }),
    clickstream_trigger: getTrigger({ triggerSourceName: 'upload_income_documents' }),
    semantic: getSemantic({ semanticEventName: 'provide_income_manually' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/4046
export const emitFinicityVoiIframeSignInAttemptEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '4046' }),
    clickstream_trigger: getTrigger({ triggerSourceName: 'submit_fi_credentials' }),
    semantic: getSemantic({ semanticEventName: 'fi_authenticate' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/4047
export const emitFinicityVoiIframeAddAccountsEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '4047' }),
    clickstream_trigger: getTrigger({ triggerSourceName: 'save_voi_account' }),
    semantic: getSemantic({ semanticEventName: 'specify_voi_accounts' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/4048
export const emitFinicityVoiIframeSubmitAccountsEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '4048' }),
    clickstream_trigger: getTrigger({ triggerSourceName: 'submit_voi_accounts' }),
    semantic: getSemantic({ semanticEventName: 'confirm_voi_accounts' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/4282
export function getClickstreamFinicityVoiIframeSelectInstitutionEvent(
  isChaseBank: boolean,
  applicationContext: ApplicationContextOrUndefined,
) {
  return trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '4282' }),
    clickstream_trigger: getTrigger({
      triggerSourceName: 'voi_fi_tile',
      triggerObjectName: isChaseBank ? 'excludes_webviews' : 'includes_webviews',
      triggerLocationName: 'voi_select_fi_page',
    }),
    semantic: getSemantic({ semanticEventName: 'select_voi_fi' }),
    ...getRentalApplication(applicationContext),
  });
}

// https://dataportal.zillowgroup.net/event-registry/4049
export const emitShareVoiIncomeStreamsEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '4049' }),
    clickstream_trigger: getTrigger({ triggerSourceName: 'continue' }),
    semantic: getSemantic({ semanticEventName: 'complete_voi_income' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/4050
export const emitSkipShareVoiIncomeStreamsEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '4050' }),
    clickstream_trigger: getTrigger({ triggerSourceName: 'upload_income_docs' }),
    semantic: getSemantic({ semanticEventName: 'skip_voi_for_manual_upload' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/4123
export const emitNextReviewIncomeSourcesEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '4123' }),
    clickstream_trigger: getTrigger({
      triggerSourceName: 'next',
      triggerLocationName: 'income_details',
      triggerObjectName: 'no_trigger_object',
    }),
    semantic: getSemantic({ semanticEventName: 'completed_income_details' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/4172
export const emitOnboardingEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '4172' }),
    clickstream_trigger: getTrigger({
      triggerLocationName: 'new_application_user_onboarding',
      triggerObjectName: 'no_trigger_object',
      triggerSourceName: 'get_started_application',
    }),
    semantic: getSemantic({ semanticEventName: 'start_applying' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/4173
export const emitVoiEligibleEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '4173' }),
    clickstream_trigger: getTrigger({
      triggerLocationName: 'income_history_landing_page',
      triggerObjectName: 'no_trigger_object',
      triggerSourceName: 'next',
    }),
    semantic: getSemantic({ semanticEventName: 'enter_income_history' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/4052
export const emitAddMoreIncomeEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '4052' }),
    clickstream_trigger: getTrigger({
      triggerLocationName: 'income_details',
      triggerObjectName: 'no_trigger_object',
      triggerSourceName: 'add_more_income',
    }),
    semantic: getSemantic({ semanticEventName: 'add_additional_income' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/2317
export const emitNextScreeningReportsEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '2317' }),
    clickstream_trigger: getTrigger({
      triggerLocationName: 'rental_application',
      triggerSourceName: 'screening_reports_next',
      triggerObjectName: 'rental_application_object_screening_section',
    }),
    semantic: getSemantic({ semanticEventName: 'next_on_screening_modal' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/2834
export const emitAuthorizeExperianEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '2834' }),
    clickstream_trigger: getTrigger({
      triggerLocationName: 'rental_application',
      triggerSourceName: 'button_to_authorize_experian',
      triggerObjectName: 'rental_application_object_screening_section',
    }),
    semantic: getSemantic({ semanticEventName: 'authorize_experian_cic' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/2039
export const emitSubmitScreeningEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '2039' }),
    clickstream_trigger: getTrigger({
      triggerLocationName: 'rental_application',
      triggerSourceName: 'button_to_submit_screening',
      triggerObjectName: 'rental_application_object_screening_section',
    }),
    semantic: getSemantic({ semanticEventName: 'submit_screening' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/2041
export const emitPrintCreditReportEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '2041' }),
    clickstream_trigger: getTrigger({
      triggerLocationName: 'rental_application',
      triggerSourceName: 'confirm_print_credit_report',
      triggerObjectName: 'rental_application_object_screening_section',
    }),
    semantic: getSemantic({ semanticEventName: 'print_credit_report' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/2042
export const emitPrintBackgroundReportEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '2042' }),
    clickstream_trigger: getTrigger({
      triggerLocationName: 'rental_application',
      triggerSourceName: 'confirm_print_background_report',
      triggerObjectName: 'rental_application_object_screening_section',
    }),
    semantic: getSemantic({ semanticEventName: 'print_background_report' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/7120
export const emitViewPaymentRequestStepEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '7120' }),
    clickstream_trigger: getTrigger({
      triggerType: 'impression',
      triggerObjectName: 'request_payments_upsell',
      triggerSourceName: 'rental_application',
    }),
    semantic: getSemantic({ semanticEventName: 'view_content' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/7146
export const emitPaymentRequestCheckEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '7146' }),
    clickstream_trigger: getTrigger({
      triggerType: 'interaction',
      triggerObjectName: 'request_payments_upsell',
      triggerSourceName: 'request_payments',
    }),
    semantic: getSemantic({ semanticEventName: 'request_payments' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/7147
export const emitNextPaymentRequestEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '7147' }),
    clickstream_trigger: getTrigger({
      triggerType: 'interaction',
      triggerObjectName: 'request_payments_upsell',
      triggerSourceName: 'request_payments_next',
    }),
    semantic: getSemantic({ semanticEventName: 'request_payments' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/7148
export const emitPaymentRequestYesEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '7148' }),
    clickstream_trigger: getTrigger({
      triggerType: 'interaction',
      triggerObjectName: 'request_payments_upsell',
      triggerSourceName: 'request_payments_yes',
    }),
    semantic: getSemantic({ semanticEventName: 'request_payments' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/7149
export const emitPaymentRequestNoEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '7149' }),
    clickstream_trigger: getTrigger({
      triggerType: 'interaction',
      triggerObjectName: 'request_payments_upsell',
      triggerSourceName: 'request_payments_no',
    }),
    semantic: getSemantic({ semanticEventName: 'request_payments' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/7150
export const emitPaymentUpsellTipViewEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '7150' }),
    clickstream_trigger: getTrigger({
      triggerType: 'impression',
      triggerObjectName: 'request_payments_upsell',
      triggerSourceName: 'rental_application',
      triggerLocationName: 'rental_application_screening_report_page',
    }),
    semantic: getSemantic({ semanticEventName: 'view_content' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/7151
export const emitPaymentUpsellTipCtaEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '7151' }),
    clickstream_trigger: getTrigger({
      triggerType: 'interaction',
      triggerObjectName: 'request_payments_upsell',
      triggerSourceName: 'request_payments',
    }),
    semantic: getSemantic({ semanticEventName: 'request_payments' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/7152
export const emitPaymentUpsellUndoEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '7152' }),
    clickstream_trigger: getTrigger({
      triggerType: 'interaction',
      triggerObjectName: 'payment_request_confirmation_modal',
      triggerSourceName: 'payment_request_undo',
    }),
    semantic: getSemantic({ semanticEventName: 'undo_request_payments' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/7153
export const emitPaymentUpsellConfirmModalImpressionEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '7153' }),
    clickstream_trigger: getTrigger({
      triggerType: 'impression',
      triggerLocationName: 'rental_application_confirmation',
      triggerObjectName: 'request_payments_upsell',
      triggerSourceName: 'rental_application_confirmation',
    }),
    semantic: getSemantic({ semanticEventName: 'view_content' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/7154
export const emitPaymentUpsellConfirmModalInteractionEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '7154' }),
    clickstream_trigger: getTrigger({
      triggerType: 'interaction',
      triggerLocationName: 'rental_application_confirmation',
      triggerObjectName: 'request_payments_upsell',
      triggerSourceName: 'request_zillow_payments',
    }),
    semantic: getSemantic({ semanticEventName: 'request_payments' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/7460
export const emitShareApplicationUpsellImpressionEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackOptimizelyEvent({
    envelope: getEnvelope({ eventTypeId: '7460' }),
    clickstream_trigger: getTrigger({
      triggerType: 'impression',
      triggerLocationName: 'rachel_application_dashboard',
      triggerObjectName: 'no_trigger_object',
      triggerSourceName: 'rachel_application_dashboard',
    }),
    semantic: getSemantic({ semanticEventName: 'view_content' }),
    ...getRentalApplication(applicationContext),
    optimizely_info: {
      test_id: null,
      test_variant: null,
    },
  });

// https://dataportal.zillowgroup.net/event-registry/7455
export const emitShareApplicationUpsellClickEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '7455' }),
    clickstream_trigger: getTrigger({
      triggerType: 'interaction',
      triggerLocationName: 'rental_application',
      triggerObjectName: 'rental_application_object',
      triggerSourceName: 'portable_clicks_share_dashboard',
    }),
    semantic: getSemantic({ semanticEventName: 'portable_clicks_share_dashboard' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/7456
export const emitShareApplicationNextEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '7456' }),
    clickstream_trigger: getTrigger({
      triggerType: 'interaction',
      triggerLocationName: 'rental_application',
      triggerObjectName: 'rental_application_object',
      triggerSourceName: 'portable_clicks_next_address_share',
    }),
    semantic: getSemantic({ semanticEventName: 'portable_clicks_next_address_share' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/7457
export const emitShareApplicationSharedEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '7457' }),
    clickstream_trigger: getTrigger({
      triggerType: 'interaction',
      triggerLocationName: 'rental_application',
      triggerObjectName: 'rental_application_object',
      triggerSourceName: 'portable_clicks_share_application',
    }),
    semantic: getSemantic({ semanticEventName: 'portable_clicks_share_application' }),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/8219
export const emitExternalScreenViewEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '8219' }),
    clickstream_trigger: {
      trigger_location_nm: 'prefill_option_page',
      trigger_type_nm: 'impression',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'prefill_option_page',
      input_selector_nm: '',
      trigger_reference_url: '',
      referral_url: '',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['3p_applications'],
    },
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/8227
export const emitExternalScreenViewIOSEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '8227' }),
    clickstream_trigger: {
      trigger_location_nm: 'prefill_option_page',
      trigger_type_nm: 'impression',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'prefill_option_page',
      input_selector_nm: '',
      trigger_reference_url: '',
      referral_url: '',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['3p_applications'],
    },
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/8228
export const emitExternalScreenViewAndroidEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '8228' }),
    clickstream_trigger: {
      trigger_location_nm: 'prefill_option_page',
      trigger_type_nm: 'impression',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'prefill_option_page',
      input_selector_nm: '',
      trigger_reference_url: '',
      referral_url: '',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['3p_applications'],
    },
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/8229
export const emitExternalScreenContinueEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: {
      event_template_id: '526',
      event_template_version_id: '1',
      event_type_id: '8229',
      event_type_version_id: '2',
      event_client_start_dtm: new Date().toISOString(),
    },
    clickstream_trigger: {
      trigger_location_nm: 'prefill_option_page',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'continue_button',
      input_selector_nm: '',
      trigger_reference_url: '',
      referral_url: '',
    },
    semantic: {
      semantic_event_nm: 'prefill_option_page',
      topic_tag_txt: ['3p_applications'],
    },
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/8231
export const emitExternalScreenContinueAndroidEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: {
      event_template_id: '526',
      event_template_version_id: '1',
      event_type_id: '8231',
      event_type_version_id: '1',
      event_client_start_dtm: new Date().toISOString(),
    },
    clickstream_trigger: {
      trigger_location_nm: 'prefill_option_page',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'continue_button',
      input_selector_nm: '',
      trigger_reference_url: '',
      referral_url: '',
    },
    semantic: {
      semantic_event_nm: 'prefill_option_page',
      topic_tag_txt: ['3p_applications'],
    },
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/8230
export const emitExternalScreenContinueIOSEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: {
      event_template_id: '526',
      event_template_version_id: '1',
      event_type_id: '8230',
      event_type_version_id: '1',
      event_client_start_dtm: new Date().toISOString(),
    },
    clickstream_trigger: {
      trigger_location_nm: 'prefill_option_page',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'continue_button',
      input_selector_nm: '',
      trigger_reference_url: '',
      referral_url: '',
    },
    semantic: {
      semantic_event_nm: 'prefill_option_page',
      topic_tag_txt: ['3p_applications'],
    },
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/8224
export const emitExternalScreenLoadingWebEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: {
      event_template_id: '526',
      event_template_version_id: '1',
      event_type_id: '8224',
      event_type_version_id: '2',
      event_client_start_dtm: new Date().toISOString(),
    },
    clickstream_trigger: {
      trigger_location_nm: '3papplications_sendoff_screen',
      trigger_type_nm: 'impression',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: '3papplications_sendoff_screen',
      input_selector_nm: '',
      trigger_reference_url: '',
      referral_url: '',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['3p_applications'],
    },
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/8225
export const emitExternalScreenLoadingIOSEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: {
      event_template_id: '526',
      event_template_version_id: '1',
      event_type_id: '8225',
      event_type_version_id: '1',
      event_client_start_dtm: new Date().toISOString(),
    },
    clickstream_trigger: {
      trigger_location_nm: '3papplications_sendoff_screen',
      trigger_type_nm: 'impression',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: '3papplications_sendoff_screen',
      input_selector_nm: '',
      trigger_reference_url: '',
      referral_url: '',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['3p_applications'],
    },
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/8226
export const emitExternalScreenLoadingAndroidEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: {
      event_template_id: '526',
      event_template_version_id: '1',
      event_type_id: '8226',
      event_type_version_id: '1',
      event_client_start_dtm: new Date().toISOString(),
    },
    clickstream_trigger: {
      trigger_location_nm: '3papplications_sendoff_screen',
      trigger_type_nm: 'impression',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: '3papplications_sendoff_screen',
      input_selector_nm: '',
      trigger_reference_url: '',
      referral_url: '',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['3p_applications'],
    },
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/8666
export const emitParticipatingPropertiesLinkEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: {
      event_template_id: '526',
      event_template_version_id: '1',
      event_type_id: '8666',
      event_type_version_id: '1',
      event_client_start_dtm: new Date().toISOString(),
    },
    clickstream_trigger: {
      trigger_location_nm: 'rental_application_interstitial',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'link_to_participating_properties',
      input_selector_nm: '',
      trigger_reference_url: '',
      referral_url: '',
    },
    semantic: {
      semantic_event_nm: 'application_interstitial_participating_properties_link',
      topic_tag_txt: ['rental_application'],
    },
    ...getRentalApplication(applicationContext),
    ...getZrmListingInfo(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/8729
export function emitViewStepEvent(applicationContext: ApplicationContextOrUndefined, stepId?: string) {
  const triggerName = getTriggerFromStepId(stepId);
  emitViewStepTriggerEvent(applicationContext, triggerName);
}

function getTriggerFromStepId(stepId?: string) {
  if (!stepId) {
    return 'review_application';
  }
  // Clicking "Begin" on IDV step
  if (stepId === 'id_verification_provide_details') {
    return 'id_verification_provide_details';
  }
  return stepId.replace(/-/g, '_');
}

export function emitViewStepTriggerEvent(applicationContext: ApplicationContextOrUndefined, triggerName: string) {
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '8729' }),
    clickstream_trigger: {
      trigger_type_nm: 'view',
      trigger_object_nm: 'no_trigger_object',
      trigger_location_nm: triggerName,
      trigger_source_nm: triggerName,
    },
    semantic: getSemantic({ semanticEventName: 'view_content' }),
    ...getRentalApplication(applicationContext),
  });
}

// https://dataportal.zillowgroup.net/event-registry/8723
export function emitNextClickEvent(applicationContext: ApplicationContextOrUndefined, stepId?: string) {
  const triggerName = getTriggerFromStepId(stepId);
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '8723' }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerName,
      trigger_location_nm: 'application_purchase_flow',
      trigger_source_nm: 'next_button',
    },
    semantic: getSemantic({ semanticEventName: 'continue_application' }),
    ...getRentalApplication(applicationContext),
  });
}

// https://dataportal.zillowgroup.net/event-registry/8683
export const emitPostSubmitParticipatingPropertiesLinkEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '8683', eventTemplateId: '526' }),
    clickstream_trigger: getTrigger({
      triggerSourceName: 'link_to_participating_properties',
      triggerLocationName: 'rental_application_post_submit',
      triggerObjectName: 'no_trigger_object',
    }),
    semantic: getSemantic({ semanticEventName: 'post_application_submit_particpating_properties_link' }),
    ...getZrmListingInfo(applicationContext),
    ...getRentalApplication(applicationContext),
  });

// https://dataportal.zillowgroup.net/event-registry/8684
export const emitPostSubmitApplyToMorePropertiesCtaEvent = (applicationContext: ApplicationContextOrUndefined) =>
  trackRentalApplicationEvent({
    envelope: getEnvelope({ eventTypeId: '8684', eventTemplateId: '526' }),
    clickstream_trigger: getTrigger({
      triggerSourceName: 'apply_to_more_properties_cta',
      triggerLocationName: 'rental_application_post_submit',
      triggerObjectName: 'no_trigger_object',
    }),
    semantic: getSemantic({
      semanticEventName: 'applications_post_submit_confirmation_page_apply_to_more_properties_cta',
    }),
    ...getRentalApplication(applicationContext),
    ...getZrmListingInfo(applicationContext),
  });
